/**
 * Безопасная периодическая проверка обновления Service Worker.
 *
 * registration.update() отклоняется с InvalidStateError («Failed to update
 * a ServiceWorker … invalid state»), если в момент вызова регистрация
 * не в рабочем состоянии: идёт установка нового воркера, активного ещё нет
 * или регистрацию сняли. Это штатная гонка, а не ошибка приложения, но
 * без обработки она уходила в журнал ошибок через unhandledrejection
 * (useErrorReporter) — из каждой открытой вкладки.
 *
 * Остальные ошибки пробрасываются, чтобы журнал их по-прежнему видел.
 */
export async function safeSwUpdate(
  registration: Pick<ServiceWorkerRegistration, 'installing' | 'active' | 'update'>,
): Promise<void> {
  // Установка уже идёт или активного воркера нет — проверять нечего
  if (registration.installing || !registration.active) return
  // Без сети update() гарантированно упадёт сетевой ошибкой
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return

  try {
    await registration.update()
  } catch (error) {
    if ((error as { name?: string } | null)?.name === 'InvalidStateError') {
      console.debug('[SW] Проверка обновления пропущена: регистрация в переходном состоянии')
      return
    }
    throw error
  }
}

/** Сколько ждём, пока новый воркер возьмёт страницу, прежде чем перезагрузить в любом случае */
export const ACTIVATE_FALLBACK_MS = 5000

/**
 * Применить обновление по кнопке «Обновить»: ожидающему воркеру напрямую
 * шлём SKIP_WAITING и перезагружаем страницу, когда он возьмёт управление.
 *
 * Обёртка vite-plugin-pwa (workbox-window) шлёт сообщение только «своему»
 * воркеру и перезагружает только по его событию — 13.10 кнопка ничего не
 * делала, плашка возвращалась, а прямой SKIP_WAITING срабатывал сразу.
 * Поэтому берём ожидающий воркер из регистрации и подстраховываемся
 * перезагрузкой по таймауту. Ожидающего нет — просто перезагружаем.
 */
/** Запись шага обновления (диагностика: какие шаги прошли до перезагрузки) */
export type UpdateTrace = (step: string) => void

type UpdateContainer = Pick<ServiceWorkerContainer, 'getRegistration' | 'addEventListener'> &
  Partial<Pick<ServiceWorkerContainer, 'startMessages' | 'controller'>>

export async function activateWaitingWorker(
  container: UpdateContainer,
  reload: () => void,
  fallbackMs = ACTIVATE_FALLBACK_MS,
  trace: UpdateTrace = () => undefined,
): Promise<void> {
  const registration = await container.getRegistration()
  const waiting = registration?.waiting
  if (!waiting) {
    trace('no-waiting')
    reload()
    return
  }
  trace(`waiting:${waiting.state ?? '?'}`)
  let reloaded = false
  const reloadOnce = (why: string) => () => {
    if (reloaded) return
    reloaded = true
    trace(why)
    reload()
  }
  // Диагностика Safari 14.10: доходит ли команда до воркера и меняет ли он
  // состояние (sw.js отвечает SKIP_WAITING_ACK)
  waiting.addEventListener?.('statechange', () => trace(`state:${waiting.state}`))
  container.addEventListener('message', (event: Event) => {
    const data = (event as MessageEvent).data
    if (data?.type === 'SKIP_WAITING_ACK') trace('ack')
    if (data?.type === 'SKIP_WAITING_RESULT') trace(String(data.result))
  })
  container.startMessages?.()
  container.addEventListener('controllerchange', reloadOnce('controllerchange'), { once: true })
  waiting.postMessage({ type: 'SKIP_WAITING' })
  trace('sent')
  setTimeout(() => {
    // Состояния на момент таймаута: кто активен, кто ждёт, кто управляет страницей
    const st = (w?: ServiceWorker | null) => (w ? w.state : '-')
    trace(
      `a:${st(registration?.active)},w:${st(registration?.waiting)},` +
        `i:${st(registration?.installing)},c:${st(container.controller)}`,
    )
    reloadOnce('fallback')()
  }, fallbackMs)
}

const ATTEMPT_KEY = 'kb.swUpdateAttempt'
/** Сколько после клика «Обновить» считаем вернувшуюся плашку неудачей */
export const ATTEMPT_MAX_AGE_MS = 2 * 60 * 1000

interface UpdateAttempt {
  at: number
  steps: string[]
}

/**
 * Запись шагов попытки обновления в sessionStorage — она переживает
 * перезагрузку вкладки, и после неё видно, на каком шаге всё остановилось.
 */
export function createUpdateRecorder(
  storage: Pick<Storage, 'setItem'> | null = safeSessionStorage(),
  now: () => number = Date.now,
): UpdateTrace {
  const at = now()
  const steps: string[] = []
  return (step) => {
    steps.push(`${step}+${now() - at}ms`)
    try {
      storage?.setItem(ATTEMPT_KEY, JSON.stringify({ at, steps }))
    } catch {
      // Приватный режим / квота — диагностика не обязательна
    }
  }
}

/** Недавняя попытка обновления (и удаление записи); null — не было или устарела */
export function takeRecentUpdateAttempt(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null = safeSessionStorage(),
  now: () => number = Date.now,
  maxAgeMs = ATTEMPT_MAX_AGE_MS,
): UpdateAttempt | null {
  try {
    const raw = storage?.getItem(ATTEMPT_KEY)
    if (!raw) return null
    storage?.removeItem(ATTEMPT_KEY)
    const attempt = JSON.parse(raw) as UpdateAttempt
    if (!Array.isArray(attempt.steps) || now() - attempt.at > maxAgeMs) return null
    return attempt
  } catch {
    return null
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}
